import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Modal, SelectChangeEvent } from "@mui/material";
import { useComponentEditorContext } from "./ComponentEditorContext";
import { useImmer } from "use-immer";
import { ClickableAreasData } from "./ClickableAreasData";
import { ClickableAreasCanvasGrid } from "./ClickableAreasCanvasGrid";
import { ContainerSizeInfo } from "./ContainerSizeInfo";
import { useObjectImages } from "./CustomHooks";
import { useModalEditorDisplayContext } from "./ModalEditorContext";
import { TreeModifyContext, useTreeDataContext, useTreeModifyContext } from "./TreeDataContext";
import { SaveAndCloseButton } from "./SaveAndCloseButton";
import { ZoomControls } from "./ZoomControls";
import { BackgroundSelector } from "./BackgroundSelector";
import { ClickableAreasArray } from "./ClickableAreasArray";
import { reducePathUntilKeyword } from "./Helpers";

export const ClickableAreasEditor: React.FC<{
  showModal: boolean;
  modalContextKey: string[];
  modalContextValue: any;
}> = ({ showModal, modalContextKey, modalContextValue }) => {
  const { onCloseModal } = useModalEditorDisplayContext();
  const { saveChangesAtPath, addGamePiece } = useTreeModifyContext();
  const { projectName, projectInfo, getVisual } = useComponentEditorContext();
  const { treeData } = useTreeDataContext();
  const [myModalContextValue, setMyModalContextValue] = useImmer(modalContextValue.value as ClickableAreasData);
  const [refElement, setRefElement] = useState<HTMLDivElement>();
  const [containerSize, setContainerSize] = useState<ContainerSizeInfo>({ width: 0, height: 0 });
  const { possibleBackgroundImages, selectedBackgroundImage, setSelectedBackgroundImage } = useObjectImages(
    modalContextKey,
    treeData,
    projectName,
    projectInfo,
  );
  const [zoom, setZoom] = useState(1);
  const width = modalContextValue.widths[0].result as number;
  const height = modalContextValue.heights[0].result as number;
  const [anyChangesMade, setAnyChangesMade] = useState(false);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (anyChangesMade) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [anyChangesMade, showModal]);

  const handleResize = useCallback(() => {
    if (refElement !== undefined) {
      setContainerSize({ height: refElement.scrollHeight, width: refElement.scrollWidth });
    }
  }, [refElement]);

  const measuredRef = useCallback(
    (node: any) => {
      if (node === null) return;
      setRefElement(node);
      handleResize();
    },
    [handleResize],
  );

  const handleBackgroundChange = (event: SelectChangeEvent) => {
    setSelectedBackgroundImage(event.target.value);
  };

  function handleCloseModal() {
    saveChangesAtPath(myModalContextValue, modalContextKey);
    onCloseModal();
  }

  const backgroundImageValue = useMemo(() => {
    if (selectedBackgroundImage) {
      return possibleBackgroundImages.find(
        (image) =>
          (image.type === "src" && image.value === selectedBackgroundImage) ||
          (image.type === "reactImg" && image.name === selectedBackgroundImage),
      );
    }
  }, [possibleBackgroundImages, selectedBackgroundImage]);

  const setAtPathInClickableAreas = useCallback(
    (value: any, path: string[]) => {
      setMyModalContextValue((draft) => {
        let reducedPath = reducePathUntilKeyword(path, "clickableAreas");
        let currentNode: any = draft;
        // Traverse the reduced path to get to the correct node, creating nodes as needed
        for (let i = 0; i < reducedPath.length - 1; i++) {
          const pathKey = reducedPath[i];
          if (!currentNode[pathKey]) {
            currentNode[pathKey] = {}; // Initialize a new node if it doesn't exist
          }
          currentNode = currentNode[pathKey];
        }

        // Set the new value at the last key of the reduced path
        currentNode[reducedPath[reducedPath.length - 1]] = value;
      });
      setAnyChangesMade(true);
    },
    [setMyModalContextValue],
  );

  const removeAtPathInClickableAreas = useCallback(
    (path: string[]) => {
      setMyModalContextValue((draft) => {
        if (!draft) {
          return;
        }

        if (path.length === 1) {
          delete draft[path[0]];
          return;
        }
        console.error("unexpected path length in removeAtPathInClickableAreas");
      });
      setAnyChangesMade(true);
    },
    [setMyModalContextValue],
  );

  const treeSaveContext = useMemo(
    () => ({
      saveChangesAtPath: setAtPathInClickableAreas,
      removeAtPath: removeAtPathInClickableAreas,
      addGamePiece,
    }),
    [addGamePiece, removeAtPathInClickableAreas, setAtPathInClickableAreas],
  );

  if (!showModal) {
    return null;
  }

  return (
    <TreeModifyContext.Provider value={treeSaveContext}>
      <Modal
        open={showModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box className="large-modal">
          <Box className="editor-layout">
            <Box className="control-panel control-panel-layout">
              <ZoomControls setZoom={setZoom} />
              <BackgroundSelector
                value={selectedBackgroundImage}
                onChange={handleBackgroundChange}
                possibleBackgroundImages={possibleBackgroundImages}
              />
              <ClickableAreasArray clickableAreas={myModalContextValue} />
              <Box className="flex-filler"></Box> {/* Spacer to fill empty space*/}
              <SaveAndCloseButton onClick={handleCloseModal} />
            </Box>
            <Box ref={measuredRef} className="canvas-grid-container">
              <ClickableAreasCanvasGrid
                backgroundImage={backgroundImageValue}
                data={myModalContextValue}
                zoom={zoom}
                containerSize={containerSize}
                width={width}
                height={height}
              />
            </Box>
          </Box>
        </Box>
      </Modal>
    </TreeModifyContext.Provider>
  );
};
